import { Location } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router, UrlCreationOptions, UrlTree } from '@angular/router';

import { Observable, Subscription, distinctUntilChanged, filter, map, shareReplay } from 'rxjs';

import { removeUrlLangPrefix, urlAfterRedirectsEqual } from '../helpers';

@Injectable({
  providedIn: 'root'
})
export class RouterHelperService implements OnDestroy {
  /**
   * Returns an observable which emits on each NavigationEnd
   * the current (last firstChild) ActivatedRoute.
   * The observable is shared for all subscribers.
   */
  public readonly activatedRoute$ = this.createActivatedRoute$();

  private urlAfterRedirects: string;
  private rawUrlAfterRedirects: string;
  private readonly navStartSubscription: Subscription;

  constructor(
    public readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly location: Location
  ) {
    this.navStartSubscription = this.subscribeToNavigationStart();
  }

  /**
   * Returns url taken from RouterStateSnapshot
   */

  public get currentUrl(): string {
    return this.router.routerState.snapshot.url;
  }

  /**
   * Returns language-sanitized url, taken from NavigationStart
   */
  public get finalUrl(): string {
    return this.urlAfterRedirects;
  }

  /**
   * Returns finalUrl, but including a possible language prefix
   */
  public get finalUrlRaw(): string {
    return this.rawUrlAfterRedirects;
  }

  // will be executed in SSR env
  ngOnDestroy(): void {
    this.navStartSubscription.unsubscribe();
  }

  /**
   * This method is to be used inside RouteGuards only,
   * in case e.g. an error occurs and an error page should be displayed,
   * but still the final target url should be shown in browser location bar,
   * as then user just can reload the page in order to try again.
   */
  public createSilentRerouteUrlTree(paths: string[], extras?: UrlCreationOptions): UrlTree {
    this.keepFinalUrl();
    return this.router.createUrlTree(paths, extras);
  }

  private keepFinalUrl(): void {
    const currentNav = this.router.getCurrentNavigation?.();

    if (currentNav) {
      // When within a route guard
      // browser location will still show previous url.
      // For being able to reload the page on target route,
      // manually set the location to the final url.
      if (this.rawUrlAfterRedirects && this.location.path() !== this.rawUrlAfterRedirects) {
        this.location.go(this.rawUrlAfterRedirects);
      }

      // We want to keep the final url and e.g. display an error instead.
      // But as `router.createUrlTree()` doesn't support NavigationExtras,
      // according to proposal in https://github.com/angular/angular/issues/35764
      // set `skipLocationChange` directly on current navigation:
      currentNav.extras.skipLocationChange = true;
    }
  }

  private createActivatedRoute$(): Observable<ActivatedRoute> {
    return this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      distinctUntilChanged<NavigationEnd>(urlAfterRedirectsEqual),
      map(() => {
        let route = this.activatedRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        return route;
      }),
      shareReplay(1)
    );
  }

  private subscribeToNavigationStart(): Subscription {
    return this.router.events.pipe(filter<NavigationStart>(e => e instanceof NavigationStart)).subscribe(({ url }) => {
      this.urlAfterRedirects = removeUrlLangPrefix(url);
      this.rawUrlAfterRedirects = url;
    });
  }
}
